import { Platform } from 'react-native';
import {
  PERSISTENT_SESSION_META_KEY,
  DEFAULT_REMEMBER_ME,
  computeSessionExpireAt,
  sessionDaysForRememberMe,
} from '@utils/sessionConstants';

export type SessionMeta = {
  rememberMe: boolean;
  expireAt: number;
  savedAt: number;
  userId?: string;
};

const canUseLocalStorage = (): boolean =>
  Platform.OS === 'web' && typeof localStorage !== 'undefined';

export function getRememberMePreference(): boolean {
  if (!canUseLocalStorage()) return DEFAULT_REMEMBER_ME;
  try {
    const raw = localStorage.getItem(PERSISTENT_SESSION_META_KEY);
    if (!raw) return DEFAULT_REMEMBER_ME;
    const meta = JSON.parse(raw) as SessionMeta;
    return meta.rememberMe ?? DEFAULT_REMEMBER_ME;
  } catch {
    return DEFAULT_REMEMBER_ME;
  }
}

export function saveRememberMePreference(rememberMe: boolean): void {
  if (!canUseLocalStorage()) return;
  try {
    const existing = readSessionMeta();
    const meta: SessionMeta = {
      rememberMe,
      expireAt: computeSessionExpireAt(rememberMe),
      savedAt: Date.now(),
      userId: existing?.userId,
    };
    localStorage.setItem(PERSISTENT_SESSION_META_KEY, JSON.stringify(meta));
  } catch (error) {
    if (__DEV__) {
      console.warn('[persistentSessionStorage] saveRememberMePreference failed', error);
    }
  }
}

export function readSessionMeta(): SessionMeta | null {
  if (!canUseLocalStorage()) return null;
  try {
    const raw = localStorage.getItem(PERSISTENT_SESSION_META_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as SessionMeta;
  } catch {
    return null;
  }
}

export function isLocalSessionMetaValid(): boolean {
  const meta = readSessionMeta();
  if (!meta?.expireAt) return false;
  return meta.expireAt > Date.now();
}

export function clearPersistentSessionMeta(): void {
  if (!canUseLocalStorage()) return;
  try {
    localStorage.removeItem(PERSISTENT_SESSION_META_KEY);
  } catch {
    /* best-effort */
  }
}

/**
 * Persist session metadata + optional userData mirror.
 * Never stores passwords or tokens — only profile mirror and TTL hints.
 */
export async function persistAuthSession(options: {
  rememberMe?: boolean;
  userId?: string;
  sessionData?: Record<string, unknown>;
}): Promise<void> {
  const rememberMe = options.rememberMe ?? getRememberMePreference();
  const expireAt = computeSessionExpireAt(rememberMe);
  const days = sessionDaysForRememberMe(rememberMe);

  const meta: SessionMeta = {
    rememberMe,
    expireAt,
    savedAt: Date.now(),
    userId: options.userId ?? (options.sessionData?.userData as { id?: string } | undefined)?.id,
  };

  if (canUseLocalStorage()) {
    try {
      localStorage.setItem(PERSISTENT_SESSION_META_KEY, JSON.stringify(meta));
    } catch (error) {
      if (__DEV__) {
        console.warn('[persistentSessionStorage] persist meta failed', error);
      }
    }
  }

  if (options.sessionData) {
    const { saveSessionToLocal } = await import(
      '@navigation/screens/TopLevel/LoginScreen/utils/authSessionHandler'
    );
    await saveSessionToLocal(
      { ...options.sessionData, rememberMe, expireAt },
      days
    );
  }
}
